import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus, Search, Download, BookOpen, MoreHorizontal, UserX, PauseCircle,
  PlayCircle, Archive, RotateCcw, Eye, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { downloadXlsx } from "@/lib/export";
import { ResidentDocumentsButton } from "@/pages/ResidentDocuments";

import { PaginationBar } from "@/components/PaginationBar";

const emptyForm = {
  // core
  name: "", email: "", phone: "", alt_phone: "", company: "",
  room_no: "", bed_no: "", room_id: "", bed_id: "", floor_id: "", building_id: "",
  status: "active",
  gender: "",
  joining_date: new Date().toISOString().slice(0, 10),
  end_date: "",
  // v23 — Subscription start/end separate from joining
  subscription_start: "",
  subscription_end: "",
  // v21.2 — Service type: 'hostel' (bed only) | 'mess' (mess only) | 'both'
  service_type: "hostel",
  // v21.1 — Decided fees (compulsory per service type)
  agreed_hostel_fee: "", agreed_mess_fee: "",
  // v23 — Authorised discount
  discount: "", discount_reason: "",
  // ids
  aadhaar: "", pan: "",
  // address
  permanent_address: "", current_address: "",
  // parent / guardian — retained on the model for backward compat only; UI removed in v23
  father_name: "", mother_name: "",
  guardian_name: "", guardian_relationship: "",
  parent_mobile: "", parent_alt_mobile: "", parent_email: "", parent_address: "",
  // emergency
  emergency_contact_name: "", emergency_contact_number: "",
};

const STATUS_VARIANT = {
  active: "default",
  suspended: "secondary",
  dropout: "destructive",
  completed: "outline",
  transferred: "outline",
  vacated: "secondary",
};

export default function Residents() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // Server-side pagination + filters + search
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get("status") || "active";
  });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [dropoutFor, setDropoutFor] = useState(null);
  const [dropoutForm, setDropoutForm] = useState({
    reason: "", date: new Date().toISOString().slice(0, 10), release_bed: true,
  });

  // Rooms + beds + floors + buildings cache for the "Bed Allotment" dropdown
  // inside the New Resident dialog. Loaded on first open so we don't hit the
  // API on every list render. Matches the Building → Floor → Room → Bed hierarchy
  // shown on the Bed Allocation page.
  const [rooms, setRooms] = useState([]);
  const [beds, setBeds] = useState([]);
  const [floors, setFloors] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [bedsLoaded, setBedsLoaded] = useState(false);
  const loadBeds = useCallback(() => {
    Promise.all([
      api.get("/rooms"), api.get("/beds"),
      api.get("/floors"), api.get("/buildings"),
    ])
      .then(([rr, bb, ff, bg]) => {
        setRooms(rr.data || []); setBeds(bb.data || []);
        setFloors(ff.data || []); setBuildings(bg.data || []);
        setBedsLoaded(true);
      })
      .catch(() => setBedsLoaded(true));
  }, []);
  useEffect(() => { if (open && !bedsLoaded) loadBeds(); }, [open, bedsLoaded, loadBeds]);

  const roomsById = Object.fromEntries(rooms.map((r) => [r.id, r]));
  const floorById = Object.fromEntries(floors.map((f) => [f.id, f]));
  const buildingById = Object.fromEntries(buildings.map((b) => [b.id, b]));
  // Available bed = status "available" AND no resident, OR the current resident's own bed (when editing).
  const bedsAvail = beds.filter((b) =>
    (b.status === "available" && !b.resident_id) ||
    (editing && (b.resident_id === editing.id || b.id === editing.bed_id))
  );
  const vacantBedsByRoom = bedsAvail.reduce((acc, b) => {
    (acc[b.room_id] ||= []).push(b); return acc;
  }, {});
  Object.values(vacantBedsByRoom).forEach((arr) =>
    arr.sort((a, b) => a.bed_no.localeCompare(b.bed_no, undefined, { numeric: true }))
  );
  // Sort rooms by building → floor → room_no so the dropdown reads naturally,
  // matching the Building → Floor → Room hierarchy on the Bed Allocation page.
  const vacantRooms = rooms
    .filter((rm) => vacantBedsByRoom[rm.id]?.length > 0)
    .sort((a, b) => {
      const bA = buildingById[a.building_id]?.name || "";
      const bB = buildingById[b.building_id]?.name || "";
      if (bA !== bB) return bA.localeCompare(bB);
      const fA = floorById[a.floor_id]?.name || "";
      const fB = floorById[b.floor_id]?.name || "";
      if (fA !== fB) return fA.localeCompare(fB);
      return a.room_no.localeCompare(b.room_no, undefined, { numeric: true });
    });

  // Full label matching the Bed Allocation card: "Room F-22 · Ground · Block A"
  const roomLabel = (rm) => {
    const bldg = buildingById[rm.building_id]?.name;
    const flr = floorById[rm.floor_id]?.name;
    const parts = [`Room ${rm.room_no}`];
    if (flr) parts.push(flr);
    if (bldg) parts.push(bldg);
    return parts.join(" · ");
  };

  const canEdit = user?.role === "admin" || user?.role === "warden" || user?.role === "hostel_manager";

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit });
    if (q.trim()) params.set("q", q.trim());
    if (statusFilter) params.set("status", statusFilter);
    return api.get(`/residents/paginated?${params.toString()}`)
      .then((r) => {
        setList(r.data.items || []);
        setTotal(r.data.total || 0);
        setTotalPages(r.data.total_pages || 1);
      })
      .catch(() => { setList([]); setTotal(0); setTotalPages(1); })
      .finally(() => setLoading(false));
  };

  // Reload when page/limit/filters change; debounce q by 300ms
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [page, limit, statusFilter]);
  useEffect(() => {
    setPage(1);
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  // Filter change also resets to page 1
  useEffect(() => { setPage(1); }, [statusFilter, limit]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, joining_date: new Date().toISOString().slice(0, 10) });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ ...emptyForm, ...Object.fromEntries(Object.entries(r).filter(([, v]) => v !== null && v !== undefined)) });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.joining_date) { toast.error("Joining date is required"); return; }
    // v43 — single-letter initials ("M", "F", "S") are valid. On EDIT, fields
    // whose value is unchanged from the stored record skip validation entirely,
    // so legacy placeholder values never block saving and are never rewritten.
    const nameRe = /^[A-Za-z][A-Za-z .'\-]{0,99}$/;
    const phoneRe = /^\+?\d{10,15}$/;
    const unchanged = (k) => editing &&
      String(form[k] ?? "").trim() === String(editing[k] ?? "").trim();
    const NAME_FIELDS = [
      ["name", "Full Name"],
      ["guardian_name", "Guardian Name"],
      ["emergency_contact_name", "Emergency Contact Name"],
      ["father_name", "Father's Name"],
      ["mother_name", "Mother's Name"],
    ];
    for (const [k, label] of NAME_FIELDS) {
      const v = String(form[k] || "").trim();
      if (v && !unchanged(k) && !nameRe.test(v)) {
        toast.error(`${label} must start with a letter and contain only letters, spaces, dots, apostrophes or hyphens.`);
        return;
      }
    }
    const PHONE_FIELDS = [
      ["phone", "Mobile Number"],
      ["alt_phone", "Alternate Mobile"],
      ["emergency_contact_number", "Emergency Contact Number"],
      ["parent_mobile", "Parent Mobile"],
      ["parent_alt_mobile", "Parent Alt Mobile"],
    ];
    for (const [k, label] of PHONE_FIELDS) {
      const raw = String(form[k] || "").trim();
      if (raw && !unchanged(k) && !phoneRe.test(raw.replace(/\s+/g, ""))) {
        toast.error(`${label} must be a valid numeric phone (10-15 digits, no letters or dashes).`);
        return;
      }
    }
    // On CREATE, enforce the full resident profile. On EDIT keep the
    // existing lenient behavior so legacy records without every field can still be updated.
    if (!editing) {
      const svc = form.service_type || "hostel";
      const REQUIRED_FIELDS = [
        ["name", "Full Name"],
        ["gender", "Gender"],
        ["company", "Company / Institution"],
        ["subscription_end", "Subscription Ends"],
        // Room/Bed only when a bed is actually used
        ...(svc !== "mess" ? [["room_no", "Room No"], ["bed_no", "Bed No"]] : []),
        // Contact — everything except PAN
        ["phone", "Mobile Number"],
        ["alt_phone", "Alternate Mobile"],
        ["email", "Email"],
        ["aadhaar", "Aadhaar Number"],
        // Address
        ["permanent_address", "Permanent Address"],
        // Emergency + Guardian (v33 — Guardian Name is now mandatory)
        ["emergency_contact_name", "Emergency Contact Name"],
        ["emergency_contact_number", "Emergency Contact Number"],
        ["guardian_name", "Guardian Name"],
        // Decided fees — required per service type
        ...(svc !== "mess"   ? [["agreed_hostel_fee", "Decided Hostel Fee"]] : []),
        ...(svc !== "hostel" ? [["agreed_mess_fee",   "Decided Mess Fee"]]   : []),
      ];
      const missing = REQUIRED_FIELDS
        .filter(([k]) => !form[k] || String(form[k]).trim() === "")
        .map(([, l]) => l);
      if (missing.length) {
        toast.error(`Please fill the required field${missing.length > 1 ? "s" : ""}: ${missing.slice(0, 3).join(", ")}${missing.length > 3 ? ` +${missing.length - 3} more` : ""}`);
        return;
      }
    }
    setSaving(true);
    try {
      // Strip empty strings so backend keeps existing values on PUT.
      // v43 — on EDIT, also drop fields identical to the stored record so legacy
      // values are never re-validated and never rewritten.
      const payload = Object.fromEntries(
        Object.entries(form).filter(([k, v]) => {
          if (v === "" || v === null || v === undefined) return false;
          if (editing && String(v).trim() === String(editing[k] ?? "").trim()) return false;
          return true;
        })
      );
      let residentId;
      if (editing) {
        await api.put(`/residents/${editing.id}`, payload);
        residentId = editing.id;
        toast.success("Resident updated");
      } else {
        const r = await api.post("/residents", payload);
        residentId = r.data.id;
        toast.success("Resident added");
      }
      // If a bed was picked in the dialog and it is different from the current one,
      // assign the resident to that bed. This also occupies the bed on the Bed Allocation page.
      if (form.bed_id && form.bed_id !== (editing?.bed_id || "")) {
        try {
          await api.post(`/beds/${form.bed_id}/assign`, { resident_id: residentId });
          toast.success(`Bed ${form.bed_no} assigned`);
        } catch (assignErr) {
          toast.error(`Bed assignment: ${formatApiError(assignErr)}`);
        }
        setBedsLoaded(false);  // force reload of vacant beds next time dialog opens
      }
      setOpen(false); setEditing(null); setForm(emptyForm); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const changeStatus = async (r, newStatus) => {
    const reason = window.prompt(`Reason for marking ${r.name} as ${newStatus}?`) || "";
    try {
      await api.post(`/residents/${r.id}/status/${newStatus}`, { reason });
      toast.success(`Marked ${newStatus}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const restore = async (r) => {
    try {
      await api.post(`/residents/${r.id}/restore`, { reason: "Restore" });
      toast.success("Restored");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const archive = async (r) => {
    if (!window.confirm(`Archive ${r.name}? Record is preserved for audit; can be restored.`)) return;
    try {
      await api.delete(`/residents/${r.id}`);
      toast.success("Archived");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitDropout = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/residents/${dropoutFor.id}/dropout`, dropoutForm);
      toast.success("Marked as dropout");
      setDropoutFor(null);
      setDropoutForm({ reason: "", date: new Date().toISOString().slice(0, 10), release_bed: true });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = list;  // server already applies search + status filter

  return (
    <div className="space-y-6" data-testid="residents-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-muted-foreground">Directory</div>
          <h1 className="font-display text-4xl font-bold mt-1">Residents</h1>
          <p className="text-muted-foreground mt-1">
            Full lifecycle: create, edit, suspend, dropout, transfer, complete, archive & restore.
          </p>
        </div>

        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => downloadXlsx("residents").catch(() => toast.error("Export failed"))}
                  data-testid="export-residents">
            <Download className="w-4 h-4 mr-2" /> Excel
          </Button>
          {canEdit && (
            <Button data-testid="add-resident-button" onClick={openCreate}>
              <Plus className="w-4 h-4 mr-2" /> Add Resident
            </Button>
          )}
        </div>
      </div>

      <Card className="p-4 border-border bg-white">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="min-w-[180px]">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="resident-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active only</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="dropout">Dropout</SelectItem>
                <SelectItem value="transferred">Transferred</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="vacated">Vacated</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
                <SelectItem value="all">All (incl. archived)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search name, phone, parent, Aadhaar, room..." className="pl-9"
              value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="residents-search" />
          </div>
          <div className="text-sm text-muted-foreground">
            {loading ? "Loading…" : `${total.toLocaleString("en-IN")} total`}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="residents-table">
            <thead>
              <tr className="text-left text-muted-foreground eyebrow border-b border-border">
                <th className="py-3 pr-4">Name</th>
                <th className="py-3 pr-4">Room</th>
                <th className="py-3 pr-4">Phone</th>
                <th className="py-3 pr-4">Joined</th>
                <th className="py-3 pr-4">Ends</th>
                <th className="py-3 pr-4">Status</th>
                <th className="py-3 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-border/50 hover:bg-secondary/40" data-testid={`resident-row-${r.id}`}>
                  <td className="py-3 pr-4 font-medium">{r.name}</td>
                  <td className="py-3 pr-4 font-mono">{r.room_no || "—"}{r.bed_no ? `-${r.bed_no}` : ""}</td>
                  <td className="py-3 pr-4 text-muted-foreground">{r.phone || "—"}</td>
                  <td className="py-3 pr-4 font-mono text-xs">{r.joining_date || "—"}</td>
                  <td className="py-3 pr-4 font-mono text-xs">{r.end_date || r.subscription_end || "—"}</td>
                  <td className="py-3 pr-4">
                    {r.archived ? (
                      <Badge variant="outline" className="text-[10px]">ARCHIVED</Badge>
                    ) : (
                      <Badge variant={STATUS_VARIANT[r.status] || "outline"} className="capitalize">
                        {r.status}
                      </Badge>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-right">
                    <div className="flex gap-2 justify-end flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => setViewing(r)}
                              data-testid={`view-${r.id}`}>
                        <Eye className="w-3 h-3 mr-1" /> View
                      </Button>
                      <ResidentDocumentsButton resident={r} />
                      <Link to={`/residents/${r.id}/ledger`}>
                        <Button size="sm" variant="outline" data-testid={`ledger-btn-${r.id}`}>
                          <BookOpen className="w-3 h-3 mr-1" /> Ledger
                        </Button>
                      </Link>
                      {canEdit && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="outline" data-testid={`actions-${r.id}`}>
                              <MoreHorizontal className="w-3 h-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {!r.archived && (
                              <DropdownMenuItem onClick={() => openEdit(r)} data-testid={`edit-${r.id}`}>
                                <Pencil className="w-3 h-3 mr-2" /> Edit
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuLabel>Lifecycle</DropdownMenuLabel>
                            {r.archived && (
                              <DropdownMenuItem onClick={() => restore(r)} data-testid={`restore-${r.id}`}>
                                <RotateCcw className="w-3 h-3 mr-2" /> Restore
                              </DropdownMenuItem>
                            )}
                            {!r.archived && r.status !== "dropout" && (
                              <DropdownMenuItem onClick={() => setDropoutFor(r)} data-testid={`dropout-${r.id}`}>
                                <UserX className="w-3 h-3 mr-2" /> Mark as Dropout
                              </DropdownMenuItem>
                            )}
                            {!r.archived && r.status === "active" && (
                              <DropdownMenuItem onClick={() => changeStatus(r, "suspended")}>
                                <PauseCircle className="w-3 h-3 mr-2" /> Suspend
                              </DropdownMenuItem>
                            )}
                            {!r.archived && r.status === "suspended" && (
                              <DropdownMenuItem onClick={() => changeStatus(r, "active")}>
                                <PlayCircle className="w-3 h-3 mr-2" /> Reactivate
                              </DropdownMenuItem>
                            )}
                            {!r.archived && ["active", "suspended"].includes(r.status) && (
                              <>
                                <DropdownMenuItem onClick={() => changeStatus(r, "completed")}>Mark Completed</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => changeStatus(r, "transferred")}>Mark Transferred</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => changeStatus(r, "vacated")}>Mark Vacated</DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuSeparator />
                            {!r.archived && user?.role === "admin" && (
                              <DropdownMenuItem onClick={() => archive(r)} className="text-destructive"
                                                data-testid={`archive-${r.id}`}>
                                <Archive className="w-3 h-3 mr-2" /> Archive (soft delete)
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="py-10 text-center text-muted-foreground">
                  {loading ? "Loading…" : q ? "No matching records found." : "No residents match."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        <PaginationBar
          page={page}
          limit={limit}
          total={total}
          totalPages={totalPages}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      </Card>

      {/* CREATE / EDIT DIALOG */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyForm); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing ? `Edit — ${editing.name}` : "New Resident"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-6">
            <Section title="Personal">
              <Field label="Full Name" required={!editing} v={form.name} on={(v) => setForm({ ...form, name: v })} tid="resident-name" />
              <div className="space-y-2">
                <Label>Gender{!editing && <span className="text-destructive"> *</span>}</Label>
                <Select value={form.gender || undefined} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger data-testid="resident-gender"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field label="Company / Institution" required={!editing} v={form.company} on={(v) => setForm({ ...form, company: v })} tid="resident-company" />
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="resident-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="dropout">Dropout</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="transferred">Transferred</SelectItem>
                    <SelectItem value="vacated">Vacated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </Section>

            <Section title="Stay">
              {/* v21.2 — Service Type gates bed picker and required decided fees */}
              <div className="space-y-2">
                <Label>Service Type<span className="text-destructive"> *</span></Label>
                <Select value={form.service_type || "hostel"}
                  onValueChange={(v) => setForm({ ...form, service_type: v,
                    // clear the opposing field when switching to a single service
                    ...(v === "mess"   ? { room_id: "", room_no: "", bed_id: "", bed_no: "", agreed_hostel_fee: "" } : {}),
                    ...(v === "hostel" ? { agreed_mess_fee: "" } : {}) })}>
                  <SelectTrigger data-testid="resident-service-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hostel">Hostel Only</SelectItem>
                    <SelectItem value="mess">Mess Only</SelectItem>
                    <SelectItem value="both">Hostel + Mess</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="hidden md:block" />
              <Field label="Joining / Check-in Date" required type="date"
                v={form.joining_date} on={(v) => setForm({ ...form, joining_date: v })} tid="resident-joining-date" />
              <Field label="End / Check-out Date" type="date"
                v={form.end_date} on={(v) => setForm({ ...form, end_date: v })} tid="resident-end-date" />
              {/* Room + Bed picker — hidden for Mess Only guests */}
              {form.service_type !== "mess" && (<>
              <div className="space-y-2">
                <Label>Room No{!editing && <span className="text-destructive"> *</span>}</Label>
                <Select value={form.room_id || ""}
                  onValueChange={(rid) => {
                    const rm = roomsById[rid];
                    setForm({ ...form,
                      room_id: rid, room_no: rm?.room_no || "",
                      bed_id: "", bed_no: "" });
                  }}
                  disabled={vacantRooms.length === 0}>
                  <SelectTrigger data-testid="resident-room">
                    <SelectValue placeholder={
                      vacantRooms.length === 0
                        ? (bedsLoaded ? "No vacant rooms — free a bed on Bed Allocation" : "Loading…")
                        : "Select a vacant room"
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {vacantRooms.map((rm) => (
                      <SelectItem key={rm.id} value={rm.id}>
                        {roomLabel(rm)} · {vacantBedsByRoom[rm.id].length} vacant
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Bed No{!editing && <span className="text-destructive"> *</span>}</Label>
                <Select value={form.bed_id || ""}
                  onValueChange={(bedId) => {
                    const b = bedsAvail.find((x) => x.id === bedId);
                    if (b) {
                      const rm = roomsById[b.room_id];
                      // v23 — Auto-fill hostel fee from Floor.monthly_fee (fallback: leave blank)
                      const fl = floorById[rm?.floor_id];
                      const autoFee = fl?.monthly_fee ? String(fl.monthly_fee) : form.agreed_hostel_fee;
                      setForm({ ...form, bed_id: b.id, bed_no: b.bed_no,
                                room_id: b.room_id, room_no: rm?.room_no || "",
                                floor_id: rm?.floor_id || "",
                                building_id: rm?.building_id || "",
                                agreed_hostel_fee: autoFee });
                    }
                  }}
                  disabled={!form.room_id}>
                  <SelectTrigger data-testid="resident-bed">
                    <SelectValue placeholder={
                      form.room_id
                        ? "Select a vacant bed"
                        : "Pick a room first"
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {(vacantBedsByRoom[form.room_id] || []).map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.bed_no}
                        {editing && editing.bed_id === b.id ? " (current)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              </>)}
              <Field label="Subscription Start" required={!editing} type="date"
                v={form.subscription_start} on={(v) => setForm({ ...form, subscription_start: v })} tid="resident-subscription-start" />
              <Field label="Subscription Ends" required={!editing} type="date"
                v={form.subscription_end} on={(v) => setForm({ ...form, subscription_end: v })} tid="resident-subscription" />
            </Section>

            {/* v21.1/21.2/23 — Decided / Agreed Fees + auto-fee-from-floor + Discount */}
            <Section title="Decided Fees">
              <div className="md:col-span-2 -mt-2 mb-1">
                <p className="text-xs text-muted-foreground">
                  Standard rate auto-loads from the Floor. Enter a Discount if authorised —
                  final fee = Standard − Discount.
                </p>
              </div>
              {form.service_type !== "mess" && (
                <Field label="Hostel Fee (₹)" required={!editing} type="number"
                  v={form.agreed_hostel_fee}
                  on={(v) => setForm({ ...form, agreed_hostel_fee: v })}
                  tid="resident-agreed-hostel" />
              )}
              {form.service_type !== "hostel" && (
                <Field label="Mess Fee (₹)" required={!editing} type="number"
                  v={form.agreed_mess_fee}
                  on={(v) => setForm({ ...form, agreed_mess_fee: v })}
                  tid="resident-agreed-mess" />
              )}
              {["admin","warden","hostel_manager"].includes(user?.role) && form.service_type !== "mess" && (<>
                <Field label="Discount (₹)" type="number"
                  v={form.discount}
                  on={(v) => setForm({ ...form, discount: v })}
                  tid="resident-discount" />
                <Field label="Discount Reason" v={form.discount_reason}
                  on={(v) => setForm({ ...form, discount_reason: v })}
                  tid="resident-discount-reason" />
              </>)}
              {/* v29 — Read-only Final Payable Fee = Hostel Fee − Discount */}
              {form.service_type !== "mess" && (Number(form.agreed_hostel_fee || 0) > 0) && (
                <div className="md:col-span-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-3"
                  data-testid="resident-final-fee">
                  <div className="text-xs text-muted-foreground mb-1">Final Payable Hostel Fee (auto-calculated)</div>
                  <div className="flex items-baseline gap-3">
                    <span className="font-display text-2xl font-bold text-primary">
                      ₹{Math.max(0, Number(form.agreed_hostel_fee || 0) - Number(form.discount || 0)).toLocaleString("en-IN")}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      = ₹{Number(form.agreed_hostel_fee || 0).toLocaleString("en-IN")} (Hostel Fee)
                      {Number(form.discount || 0) > 0 && (
                        <> − ₹{Number(form.discount).toLocaleString("en-IN")} (Discount)</>
                      )}
                    </span>
                  </div>
                </div>
              )}
            </Section>

            <Section title="Contact">
              <Field label="Mobile Number" required={!editing} v={form.phone} on={(v) => setForm({ ...form, phone: v })} tid="resident-phone" />
              <Field label="Alternate Mobile" required={!editing} v={form.alt_phone} on={(v) => setForm({ ...form, alt_phone: v })} tid="resident-alt-phone" />
              <Field label="Email" required={!editing} type="email" v={form.email} on={(v) => setForm({ ...form, email: v })} tid="resident-email" />
              <Field label="Aadhaar Number" required={!editing} v={form.aadhaar} on={(v) => setForm({ ...form, aadhaar: v })} tid="resident-aadhaar" />
              <Field label="PAN Number" v={form.pan} on={(v) => setForm({ ...form, pan: v })} tid="resident-pan" />
            </Section>

            <Section title="Address" span2>
              <div className="space-y-2 md:col-span-2">
                <Label>Permanent Address{!editing && <span className="text-destructive"> *</span>}</Label>
                <Textarea rows={2} value={form.permanent_address}
                  onChange={(e) => setForm({ ...form, permanent_address: e.target.value })}
                  data-testid="resident-permanent-address" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Current Address</Label>
                <Textarea rows={2} value={form.current_address}
                  onChange={(e) => setForm({ ...form, current_address: e.target.value })}
                  data-testid="resident-current-address" />
              </div>
            </Section>

            <Section title="Emergency Contact">
              <Field label="Contact Person Name" required={!editing} v={form.emergency_contact_name} on={(v) => setForm({ ...form, emergency_contact_name: v })} tid="resident-emergency-name" />
              <Field label="Contact Number" required={!editing} v={form.emergency_contact_number} on={(v) => setForm({ ...form, emergency_contact_number: v })} tid="resident-emergency-number" />
              <Field label="Relationship" v={form.guardian_relationship || ""} on={(v) => setForm({ ...form, guardian_relationship: v })} tid="resident-emergency-relationship" />
              <Field label="Guardian Name" required={!editing} v={form.guardian_name} on={(v) => setForm({ ...form, guardian_name: v })} tid="resident-guardian-name" />
            </Section>

            <DialogFooter>
              <Button type="submit" disabled={saving} data-testid="resident-save">
                {saving ? "Saving..." : editing ? "Update Resident" : "Save Resident"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* PROFILE VIEW DIALOG */}
      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{viewing?.name}</DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-6 text-sm">
              <ProfileGrid title="Personal" rows={[
                ["Gender", viewing.gender],
                ["Company / Institution", viewing.company],
                ["Status", viewing.archived ? "ARCHIVED" : viewing.status],
              ]} />
              <ProfileGrid title="Stay" rows={[
                ["Joining Date", viewing.joining_date],
                ["End Date", viewing.end_date],
                ["Room", `${viewing.room_no || "—"}${viewing.bed_no ? "-" + viewing.bed_no : ""}`],
                ["Subscription Ends", viewing.subscription_end],
              ]} />
              <ProfileGrid title="Contact" rows={[
                ["Mobile", viewing.phone],
                ["Alt Mobile", viewing.alt_phone],
                ["Email", viewing.email],
                ["Aadhaar", viewing.aadhaar],
                ["PAN", viewing.pan],
              ]} />
              <ProfileGrid title="Address" rows={[
                ["Permanent", viewing.permanent_address],
                ["Current", viewing.current_address],
              ]} />
              <ProfileGrid title="Parent / Guardian" rows={[
                ["Father", viewing.father_name],
                ["Mother", viewing.mother_name],
                ["Guardian", viewing.guardian_name],
                ["Relationship", viewing.guardian_relationship],
                ["Parent Mobile", viewing.parent_mobile],
                ["Parent Alt Mobile", viewing.parent_alt_mobile],
                ["Parent Email", viewing.parent_email],
                ["Parent Address", viewing.parent_address],
              ]} />
              <ProfileGrid title="Emergency Contact" rows={[
                ["Name", viewing.emergency_contact_name || viewing.emergency_contact],
                ["Number", viewing.emergency_contact_number],
              ]} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* DROPOUT DIALOG */}
      <Dialog open={!!dropoutFor} onOpenChange={(v) => !v && setDropoutFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Mark {dropoutFor?.name} as Dropout
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitDropout} className="space-y-4">
            <div className="space-y-2">
              <Label>Dropout Reason *</Label>
              <Textarea required rows={3} value={dropoutForm.reason}
                onChange={(e) => setDropoutForm({ ...dropoutForm, reason: e.target.value })}
                data-testid="dropout-reason" />
            </div>
            <div className="space-y-2">
              <Label>Dropout Date *</Label>
              <Input required type="date" value={dropoutForm.date}
                onChange={(e) => setDropoutForm({ ...dropoutForm, date: e.target.value })}
                data-testid="dropout-date" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={dropoutForm.release_bed}
                onChange={(e) => setDropoutForm({ ...dropoutForm, release_bed: e.target.checked })}
                data-testid="dropout-release-bed" />
              <span className="text-sm">Release the allocated bed</span>
            </label>
            <p className="text-xs text-muted-foreground">
              Future fee generation for this resident will be automatically blocked.
            </p>
            <DialogFooter>
              <Button type="submit" variant="destructive" data-testid="dropout-save">Confirm Dropout</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, v, on, type = "text", required, tid }) => (
  <div className="space-y-2">
    <Label>{label}{required && " *"}</Label>
    <Input type={type} value={v || ""} onChange={(e) => on(e.target.value)} required={required} data-testid={tid} />
  </div>
);

const Section = ({ title, span2, children }) => (
  <div>
    <div className="eyebrow text-muted-foreground mb-3">{title}</div>
    <div className={`grid grid-cols-1 ${span2 ? "md:grid-cols-2" : "md:grid-cols-2"} gap-4`}>
      {children}
    </div>
  </div>
);

const ProfileGrid = ({ title, rows }) => (
  <div>
    <div className="eyebrow text-muted-foreground mb-2">{title}</div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 border border-border rounded-md p-4 bg-secondary/30">
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-4 py-1 border-b border-border/40 last:border-b-0">
          <span className="text-muted-foreground">{k}</span>
          <span className="font-medium text-right">{v || "—"}</span>
        </div>
      ))}
    </div>
  </div>
);
